import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { environment } from '@accedo/xdk-core';
import { vKey } from '@accedo/xdk-virtual-key';
import { focusManager } from '@accedo/vdkweb-navigation';
import { isOpen, getModal } from '#/redux/modules/modal/selectors';
import { exitDeeplinkWorkflow } from '#/redux/modules/lifecycle/actions';
import {
  getDeeplinkOutsidePlayer,
  getDeepLinkStarted,
  getEndDeeplinkOnBackNav,
} from '#/redux/modules/lifecycle/selectors';
import routes, { mainRoutes, authFlowRoutes } from '#/utils/routes';
import { HEADER, HOME } from '#/utils/navigationHelper';

import { toggleSpinner } from '#/redux/modules/spinner/actions';
import { isTizen, isWorkstation, isVIZIO } from '#/utils/componentStyleConfig';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import playerModalstyles from '#/components/PlayerModal/playerModal.scss';
import { logout } from '#/redux/modules/identity/identityGuest/actions';
import { getParentUserId } from '#/redux/modules/identity/selectors';
import { actions as identityActions } from '#/redux/modules/identity/actions';
import { actions as onboardingActions } from '#/redux/modules/onboarding/actions';
import { setExitFromPlayer } from '#/redux/modules/appPlayer/actions';
import {
  getBackgroundPlayerFullscreen,
  getExitFromPlayer,
} from '#/redux/modules/appPlayer/selectors';
import {
  getCurrentProfileDeepLink,
  getCurrentProfileId,
} from '#/redux/modules/onboarding/selectors';
import { isGuestMode } from '#/redux/modules/user/selectors';
import { signupStates } from '#/config/constants';

const { BACK, EXIT } = vKey;

const THROTTLE_TIME = 350;
let lastPressed = Date.now();
const HOME_RETURN_FOCUS_ID = 'sensicalHomeReturnFocusId';

const Route = ({ content, location, history }) => {
  const dispatch = useDispatch();
  const isModalOpen = useSelector(state => isOpen(state));
  const getModalId = useSelector(state => getModal(state));
  const exitFromPlayer = useSelector(state => getExitFromPlayer(state));
  const guestMode = useSelector(isGuestMode);
  const isBackgroundPlayerFullscreen = useSelector(state =>
    getBackgroundPlayerFullscreen(state),
  );
  const deepLinked = useSelector(getCurrentProfileDeepLink);
  const deeplinkOutsidePlayer = useSelector(getDeeplinkOutsidePlayer);
  const profileId = useSelector(getCurrentProfileId);
  const parentUserId = useSelector(getParentUserId);
  const deepLinkStarted = useSelector(getDeepLinkStarted);
  const endDeeplinkOnBackNav = useSelector(getEndDeeplinkOnBackNav);

  const latestisBackgroundPlayerFullscreen = useRef(
    isBackgroundPlayerFullscreen,
  );
  const checkPlayerModalContainer = useRef(null);

  useEffect(() => {
    // spinner shows up for main routes only
    if (location.pathname.match(mainRoutes.ROOT)) {
      dispatch(toggleSpinner(true));
    } else if (!location.pathname.match(mainRoutes.ROOT)) {
      requestAnimationFrame(() => {
        dispatch(toggleSpinner(false));
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const playerModalContainer = document
      .getElementsByClassName(playerModalstyles?.container)[0]
      ?.firstElementChild?.classList?.contains(
        playerModalstyles?.backgroundPlayer,
      );
    checkPlayerModalContainer.current = playerModalContainer;
    latestisBackgroundPlayerFullscreen.current = isBackgroundPlayerFullscreen;
    if (
      latestisBackgroundPlayerFullscreen.current &&
      checkPlayerModalContainer.current &&
      location.pathname === mainRoutes.HOME
    ) {
      dispatch(setExitFromPlayer(true));
    } else if (
      !latestisBackgroundPlayerFullscreen.current &&
      !checkPlayerModalContainer.current &&
      location.pathname === mainRoutes.HOME
    ) {
      setTimeout(() => {
        dispatch(setExitFromPlayer(false));
      }, 200);
    }
  }, [isBackgroundPlayerFullscreen, checkPlayerModalContainer.current]);

  const openCloseModal = useCallback(() => {
    return dispatch(
      modalActions.openModal({
        modalId: 'ExitModal',
        successAction: () => {
          if (isTizen()) {
            /* eslint-disable eslint-comments/no-unlimited-disable */
            /* eslint-disable */
            tizen?.application?.getCurrentApplication()?.exit();
            /* eslint-enable */
          } else if (isVIZIO()) {
            window.VIZIO.exitApplication();
          } else {
            history.goBack();
          }
        },
      }),
    );
  }, []);

  const backKeyHandler = useCallback(
    ({ id }) => {
      const now = Date.now();

      if (now - lastPressed < THROTTLE_TIME) {
        return;
      }

      const playerModalContainer = document
        .getElementsByClassName(playerModalstyles?.container)[0]
        ?.firstElementChild?.classList?.contains(
          playerModalstyles?.backgroundPlayer,
        );
      checkPlayerModalContainer.current = playerModalContainer;

      // Disable back presses on web or if modal open
      if ((isWorkstation() || isModalOpen) && getModalId !== 'WelcomeModal') {
        return;
      }

      // Modal for exiting the app when EXIT is pressed
      if (id === EXIT.id) {
        openCloseModal();
        return;
      }

      if (id === BACK.id) {
        // Handle deeplinking exits
        if (deepLinkStarted) {
          const fromPlayer =
            location.pathname.includes('/livetv') ||
            location.pathname.includes('/player');

          if (fromPlayer) {
            // if deeplinked from anywhere but the player itself
            if (deeplinkOutsidePlayer || !endDeeplinkOnBackNav) {
              history.goBack();
              return;
            }

            dispatch(exitDeeplinkWorkflow());

            if (deepLinked && guestMode) {
              dispatch(logout(authFlowRoutes.GUEST_PROFILE));
            } else if (
              profileId === parentUserId &&
              parentUserId &&
              profileId
            ) {
              dispatch(identityActions.setParentMode(true));
            }

            history.goBack();
            return;
          }

          if (profileId === parentUserId && parentUserId && profileId) {
            dispatch(identityActions.setParentMode(true));
          }

          if (endDeeplinkOnBackNav) {
            dispatch(exitDeeplinkWorkflow());
          }

          history.goBack();
          return;
        }

        if (getModalId === 'WelcomeModal') {
          dispatch(modalActions.closeModal());
          return;
        }

        // Disable back presses on the standalone player page, handled by appActions.exitPlayback
        if (location.pathname === routes.BASE_PLAYER) {
          return;
        }

        // Disable this back options when in player
        if (location.pathname === mainRoutes.HOME && exitFromPlayer) {
          return;
        }

        if (
          isTizen() &&
          location.pathname === mainRoutes.HOME &&
          !isBackgroundPlayerFullscreen &&
          !playerModalContainer
        ) {
          const currentFocus = focusManager.getCurrentFocus();
          if (currentFocus !== HEADER.MENU_ITEMS.HOME) {
            focusManager.changeFocus(HEADER.MENU_ITEMS.HOME);
            return;
          }
        }

        // Modal for exiting the app when BACK is pressed
        if (
          (location.pathname === mainRoutes.HOME &&
            !isBackgroundPlayerFullscreen &&
            !playerModalContainer) ||
          location.pathname === authFlowRoutes.WELCOME ||
          location.pathname === authFlowRoutes.GUEST_PROFILE ||
          location.pathname === routes.PROFILE_SELECTION
        ) {
          openCloseModal();
          return;
        }

        if (
          location.pathname === mainRoutes.HOME &&
          isBackgroundPlayerFullscreen &&
          playerModalContainer
        ) {
          return;
        }

        if (history.location.state?.fromMainPage) {
          dispatch(
            onboardingActions.setSignupState({
              signupState: signupStates.GUEST_WELCOME,
            }),
          );
          dispatch(logout(authFlowRoutes.GUEST_PROFILE));
          return;
        }

        if (isTizen() && location.pathname.includes(mainRoutes.LIVETV)) {
          history.replace(mainRoutes.HOME);
          setTimeout(() => {
            focusManager.changeFocus(HOME.PAGE);
            focusManager.changeFocus(HOME.CONTAINER);
          }, 120);
          return;
        }

        if (
          isTizen() &&
          (location.pathname.includes('/showdetails') ||
            location.pathname.includes('/creatordetails'))
        ) {
          const returnFocusId = history.location.state?.returnFocusId;
          if (returnFocusId) {
            window.sessionStorage.setItem(HOME_RETURN_FOCUS_ID, returnFocusId);
          }
        }

        history.goBack();
      }

      lastPressed = now;
    },
    [
      location.pathname,
      isModalOpen,
      deepLinked,
      deeplinkOutsidePlayer,
      profileId,
      parentUserId,
      deepLinkStarted,
      guestMode,
      isBackgroundPlayerFullscreen,
      exitFromPlayer,
      endDeeplinkOnBackNav,
      getModalId,
    ],
  );

  useEffect(() => {
    if (!isWorkstation()) {
      environment.addEventListener(environment.SYSTEM.KEYDOWN, backKeyHandler);
      return () => {
        environment.removeEventListener(
          environment.SYSTEM.KEYDOWN,
          backKeyHandler,
        );
      };
    }
  }, [isModalOpen, backKeyHandler]);

  return <>{content}</>;
};

Route.propTypes = {
  history: PropTypes.object,
  content: PropTypes.any,
  location: PropTypes.object,
};

export default withRouter(Route);
